import { type ReactNode, useLayoutEffect, useRef } from "react";

type Box = { x: number; y: number; w: number; h: number };

/**
 * Where each tab bar's pill last sat. Some tabs are separate pages (Empresa → Ubicaciones),
 * so the bar remounts; starting from here lets the pill still slide instead of popping in.
 */
const lastBox = new Map<string, Box>();

function move(pill: HTMLElement, b: Box) {
  pill.style.transform = `translate(${b.x}px, ${b.y}px)`;
  pill.style.width = `${b.w}px`;
  pill.style.height = `${b.h}px`;
  pill.style.opacity = "1";
}

/** Places the pill without animating (first paint, resizes). */
function jump(pill: HTMLElement, b: Box) {
  pill.style.transition = "none";
  move(pill, b);
  pill.getBoundingClientRect();
  pill.style.transition = "";
}

/**
 * The pill-shaped tab bar. Tabs are links marked with aria-current="page"; a single
 * highlight slides from the old tab to the new one.
 */
export function TabNav({ label, children }: { label: string; children: ReactNode }) {
  const nav = useRef<HTMLElement>(null);
  const pill = useRef<HTMLSpanElement>(null);

  useLayoutEffect(() => {
    const el = nav.current;
    const p = pill.current;
    if (!el || !p) return;
    const current = (): Box | null => {
      const a = el.querySelector<HTMLElement>('[aria-current="page"]');
      return a ? { x: a.offsetLeft, y: a.offsetTop, w: a.offsetWidth, h: a.offsetHeight } : null;
    };
    const place = (animate: boolean) => {
      const b = current();
      if (!b) {
        p.style.opacity = "0";
        return;
      }
      if (animate) move(p, b);
      else jump(p, b);
      lastBox.set(label, b);
    };

    // A new page paints its first frame before it is fully shown; slide after it.
    const from = lastBox.get(label);
    let frame = 0;
    if (from) {
      jump(p, from);
      frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => place(true));
      });
    } else place(false);

    const onTab = new MutationObserver(() => place(true));
    onTab.observe(el, { subtree: true, attributes: true, attributeFilter: ["aria-current"] });
    // Fires once right away too; only a real change of size should skip the slide.
    let size = `${el.offsetWidth}x${el.offsetHeight}`;
    const onResize = new ResizeObserver(() => {
      const now = `${el.offsetWidth}x${el.offsetHeight}`;
      if (now === size) return;
      size = now;
      place(false);
    });
    onResize.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      onTab.disconnect();
      onResize.disconnect();
    };
  }, [label]);

  return (
    <nav ref={nav} className="kb-tabs" aria-label={label}>
      <span ref={pill} className="kb-tabs-pill" aria-hidden="true" />
      {children}
    </nav>
  );
}
